import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Section, GlassCard, Eyebrow, StatBlock } from "../components/Section";
import {
  Users,
  MessageSquare,
  Briefcase,
  Wallet,
  Mail,
  Activity,
  Search,
  ArrowRight,
  Loader2,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Activity },
  { id: "users", label: "Sign-ups", icon: Users },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "lawyers", label: "Lawyers", icon: Briefcase },
  { id: "requests", label: "Cases", icon: MessageSquare },
  { id: "wallet", label: "Justice Wallet", icon: Wallet },
];

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [walletApps, setWalletApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchTab(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, user]);

  const fetchTab = async (tab) => {
    setBusy(true);
    try {
      if (tab === "overview") {
        const { data } = await axios.get(`${API}/admin/overview`, { withCredentials: true });
        setOverview(data);
      } else if (tab === "users") {
        const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(data.users);
      } else if (tab === "contacts") {
        const { data } = await axios.get(`${API}/admin/contacts`, { withCredentials: true });
        setContacts(data.contacts);
      } else if (tab === "lawyers") {
        const { data } = await axios.get(`${API}/admin/lawyers`, { withCredentials: true });
        setLawyers(data.lawyers);
      } else if (tab === "requests") {
        const { data } = await axios.get(`${API}/admin/requests`, { withCredentials: true });
        setRequests(data.requests);
      } else if (tab === "wallet") {
        const { data } = await axios.get(`${API}/admin/wallet-applications`, { withCredentials: true });
        setWalletApps(data.applications);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load admin data");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink">
        <Loader2 className="w-8 h-8 animate-spin text-turquoise" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <main className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-ink text-white px-6">
        <div className="text-center max-w-md">
          <Eyebrow>Access denied</Eyebrow>
          <h1 className="font-playfair text-4xl text-white mt-3 mb-3">Admin only</h1>
          <p className="font-inter text-sm text-white/60 mb-6">
            This area is restricted to AccessJustice administrators.
          </p>
          <button onClick={() => navigate("/dashboard")} data-testid="admin-back-btn" className="btn-aj-primary">
            Back to Dashboard <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    );
  }

  const filtered = (rows, keys) => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => keys.some((k) => String(r[k] || "").toLowerCase().includes(q)));
  };

  return (
    <main className="flex-1 bg-ink text-white min-h-[calc(100vh-5rem)] py-10" data-testid="admin-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <Eyebrow>Admin Console</Eyebrow>
          <h1 className="font-playfair text-3xl md:text-5xl text-white mt-3 leading-tight">
            Platform <span className="italic text-aj-gradient">control room</span>.
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar tabs */}
          <aside className="lg:col-span-3">
            <div className="glass rounded-2xl p-3 space-y-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setActive(t.id); setSearch(""); }}
                  data-testid={`admin-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-montserrat text-xs uppercase tracking-wider transition-all ${
                    active === t.id
                      ? "bg-turquoise text-ink font-semibold"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <section className="lg:col-span-9">
            {busy && (
              <div className="flex items-center gap-2 text-white/55 mb-4 font-inter text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            )}

            {active === "overview" && overview && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <GlassCard><StatBlock value={overview.total_users} label="Total Sign-ups" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_lawyers} label="Lawyer Profiles" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_subscribed_lawyers} label="Lawyer Subscriptions" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_contacts} label="Contact Submissions" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_wallet_applications} label="Wallet Applications" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_chat_messages} label="AI Chat Turns" /></GlassCard>
                  <GlassCard><StatBlock value={overview.total_lawyer_requests} label="Lawyer Requests" /></GlassCard>
                  <GlassCard><StatBlock value={overview.open_lawyer_requests} label="Open Requests" /></GlassCard>
                </div>
                <GlassCard>
                  <Eyebrow className="mb-4">Sign-ups by Role</Eyebrow>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(overview.by_role || {}).map(([k, v]) => (
                      <div key={k} className="rounded-xl border border-white/8 bg-white/[0.025] px-4 py-3">
                        <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/45">{k}</div>
                        <div className="font-playfair italic text-3xl text-aj-gradient mt-1">{v}</div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </div>
            )}

            {/* Search input for list tabs */}
            {active !== "overview" && (
              <div className="mb-4 relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  data-testid="admin-search"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-2.5 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all"
                />
              </div>
            )}

            {active === "users" && (
              <GlassCard className="!p-0 overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/8">
                      <th className="px-5 py-3 font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50">Name</th>
                      <th className="px-5 py-3 font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50">Email</th>
                      <th className="px-5 py-3 font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50">Role</th>
                      <th className="px-5 py-3 font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered(users, ["name", "email", "role"]).map((u, i) => (
                      <motion.tr
                        key={u.user_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, delay: i * 0.02 }}
                        className="border-b border-white/5 hover:bg-white/[0.025]"
                        data-testid={`admin-user-row-${i}`}
                      >
                        <td className="px-5 py-3 font-inter text-sm text-white">{u.name}</td>
                        <td className="px-5 py-3 font-inter text-sm text-white/70">{u.email}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full font-montserrat text-[10px] uppercase tracking-wider ${
                            u.role === "admin" ? "bg-gold-400/15 text-gold-200 border border-gold-400/30"
                            : u.role === "lawyer" ? "bg-turquoise/10 text-turquoise border border-turquoise/30"
                            : "bg-white/5 text-white/60 border border-white/10"
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-5 py-3 font-inter text-xs text-white/50">
                          {new Date(u.created_at).toLocaleString()}
                        </td>
                      </motion.tr>
                    ))}
                    {users.length === 0 && !busy && (
                      <tr><td colSpan={4} className="px-5 py-8 text-center font-inter text-sm text-white/40">No users yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </GlassCard>
            )}

            {active === "contacts" && (
              <div className="space-y-3">
                {filtered(contacts, ["name", "email", "organization", "message"]).map((c, i) => (
                  <GlassCard key={c.contact_id || i} data-testid={`admin-contact-${i}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h4 className="font-playfair text-lg text-white">{c.name}</h4>
                        <div className="font-inter text-xs text-white/55">{c.email} {c.organization ? `· ${c.organization}` : ""}</div>
                      </div>
                      <span className="aj-chip !text-[9px] !py-1 !px-2.5">{c.submission_type || "general"}</span>
                    </div>
                    <p className="font-inter text-sm text-white/75 leading-relaxed">{c.message}</p>
                    <div className="font-inter text-[11px] text-white/35 mt-3">{new Date(c.created_at).toLocaleString()}</div>
                  </GlassCard>
                ))}
                {contacts.length === 0 && !busy && (
                  <p className="font-inter text-sm text-white/40 text-center py-12">No contact submissions yet.</p>
                )}
              </div>
            )}

            {active === "lawyers" && (
              <div className="space-y-3">
                {filtered(lawyers, ["bar_number", "location", "bio"]).map((l, i) => (
                  <GlassCard key={l.user_id || i} data-testid={`admin-lawyer-${i}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-playfair text-lg text-white">Bar #{l.bar_number}</h4>
                        <div className="font-inter text-xs text-white/55 mb-2">{l.location} · {(l.practice_areas || []).join(", ")}</div>
                        <p className="font-inter text-sm text-white/70 leading-relaxed">{l.bio}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full font-montserrat text-[10px] uppercase tracking-wider ${
                          l.is_verified ? "bg-turquoise/10 text-turquoise border border-turquoise/30" : "bg-gold-400/10 text-gold-200 border border-gold-400/30"
                        }`}>
                          {l.is_verified ? "Verified" : "Pending"}
                        </span>
                        {l.subscription && (
                          <div className="font-inter text-[10px] text-white/45 mt-2">
                            {l.subscription.plan_label}<br />
                            <span className="text-gold-300">{l.subscription.billing_status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </GlassCard>
                ))}
                {lawyers.length === 0 && !busy && (
                  <p className="font-inter text-sm text-white/40 text-center py-12">No lawyer profiles yet.</p>
                )}
              </div>
            )}

            {active === "requests" && (
              <div className="space-y-3">
                {filtered(requests, ["citizen_name", "category", "summary", "location"]).map((r, i) => (
                  <GlassCard key={r.request_id} data-testid={`admin-request-${i}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h4 className="font-playfair text-lg text-white">{r.category} · {r.citizen_name}</h4>
                        <div className="font-inter text-xs text-white/55">{r.location} · {r.urgency.toUpperCase()} urgency · Match {r.match_score || "—"}%</div>
                      </div>
                      <span className={`aj-chip !text-[9px] !py-1 ${r.status === "open" ? "" : "aj-chip-gold"}`}>{r.status}</span>
                    </div>
                    <p className="font-inter text-sm text-white/75 leading-relaxed">{r.summary}</p>
                    <div className="font-inter text-[11px] text-white/35 mt-3">
                      {new Date(r.created_at).toLocaleString()} {r.assigned_lawyer_name && `· Assigned to ${r.assigned_lawyer_name}`}
                    </div>
                  </GlassCard>
                ))}
                {requests.length === 0 && !busy && (
                  <p className="font-inter text-sm text-white/40 text-center py-12">No lawyer requests yet.</p>
                )}
              </div>
            )}

            {active === "wallet" && (
              <div className="space-y-3">
                {walletApps.map((w, i) => (
                  <GlassCard key={i} data-testid={`admin-wallet-${i}`}>
                    <pre className="font-inter text-xs text-white/70 whitespace-pre-wrap">{JSON.stringify(w, null, 2)}</pre>
                  </GlassCard>
                ))}
                {walletApps.length === 0 && !busy && (
                  <p className="font-inter text-sm text-white/40 text-center py-12">No Justice Wallet applications yet.</p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
